/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Injector } from '@angular/core';
import {
  AuthNumberGenerationConfig,
  authNumberGenerationDialog,
  BuilderUserContext,
} from '@gh/builder-shared';
import {
  ApplicationView,
  BuilderPermission,
  BuilderPermissionSet,
  SystemRoleBit,
} from '@gh/builder-shared-data';
import { NavigationItemTree, RootLayout } from '@gh/common-internal-shared';
import { TranslationService } from '@gh/core-mls';
import { DialogService } from '@gh/core-ui';

import { ColorSchemePickerComponent } from '../components';
import { BillingInvoicesRedirectBusinessService } from '@gh/builder-setup';

export function buildBuilderNavigationMenu(userContext: BuilderUserContext, injector: Injector): NavigationItemTree[] {
  switch (userContext.applicationView) {
    case ApplicationView.Corporate:
    case ApplicationView.Master:
      return buildAdminNavigationMenu(userContext, injector);
    default:
      return buildUserNavigationMenu(userContext, injector);
  }
}
function isCorpOrMaster(userContext: BuilderUserContext): boolean {
  return userContext.hasSystemRole(SystemRoleBit.Corporate) || userContext.hasSystemRole(SystemRoleBit.Master);
}

function buildUserNavigationMenu(userContext: BuilderUserContext, injector: Injector): NavigationItemTree[] {
  return [{
    name: 'dashboard',
    icon: 'gh:dashboard',
    title: 'lbl_menu_dashboard',
    route: 'dashboard',
  }, {
    name: 'sales',
    icon: 'gh:money-off',
    title: 'lbl_menu_sales',
    enabled: userContext.hasPermission(BuilderPermission.Sales.NESTED),
    children: [
      {
        name: 'leads',
        route: 'sales/leads',
        reuseRoute: false,
        title: 'lbl_leads',
        enabled: userContext.hasPermission(BuilderPermission.Sales.Leads.LIST),
      },
      {
        name: 'leads',
        route: 'sales/leads-board',
        reuseRoute: false,
        title: 'lbl_leads_board',
        enabled: userContext.hasPermission(BuilderPermission.Sales.Leads.LIST),
      },
      {
        name: 'rfq-management',
        route: 'sales/rfq-management',
        reuseRoute: false,
        title: 'lbl_rfq_management',
        enabled: userContext.hasPermission(BuilderPermission.Sales.Leads.LIST),
      },
      {
        name: 'hlp',
        route: 'sales/hlp',
        reuseRoute: false,
        title: 'lbl_hl',
        enabled: userContext.hasPermission(BuilderPermission.Sales.Hlp.LIST),
      },
      {
      name: 'salesperson-budgets',
      route: 'sales/salesperson-budgets',
      title: 'lbl_budgets',
      enabled: userContext.hasPermission(BuilderPermission.Sales.Budgets.VIEW)
        || userContext.hasPermission(BuilderPermission.Sales.Budgets.EDIT),
    }],
  }, {
    name: 'job-admin',
    icon: 'gh:power-settings-new',
    title: 'lbl_jobs',
    enabled: userContext.hasPermission(BuilderPermission.JobAdmin.NESTED),
    children: [{
      name: 'jobs',
      reuseRoute: false,
      route: 'job-admin/jobs',
      title: 'lbl_job_list',
      enabled: userContext.hasPermission(BuilderPermission.JobAdmin.Jobs.LIST),
    }, {
      name: 'job-financials',
      reuseRoute: false,
      route: 'job-admin/job-financials',
      title: 'lbl_job_financials',
      enabled: userContext.hasPermission(BuilderPermission.JobAdmin.Financials.LIST),
    }, {
      name: 'jobs-co',
      route: 'job-admin/job-change-orders',
      reuseRoute: false,
      title: 'lbl_job_change_orders',
      enabled: userContext.hasPermission(BuilderPermission.JobAdmin.JobChangeOrders.LIST),
    }, {
      name: 'job-orders',
      route: 'job-admin/job-orders',
      reuseRoute: false,
      title: 'lbl_job_orders',
      enabled: userContext.hasPermission(BuilderPermissionSet.JobAdmin.JobOrders.LIST),
    }, {
      name: 'job-specifications',
      route: 'job-admin/job-specifications',
      reuseRoute: false,
      title: 'lbl_job_specifications',
      enabled: userContext.hasPermission(BuilderPermissionSet.JobAdmin.JobSpecifications.LIST),
    }, {
      name: 'job-color-selection',
      route: 'job-admin/job-color-selection',
      reuseRoute: false,
      title: 'lbl_color_selections',
      enabled: userContext.hasPermission(BuilderPermissionSet.JobAdmin.JobColorSelection.LIST),
    }],
  }, {
    name: 'accounting',
    icon: 'gh:repeat',
    title: 'lbl_menu_accounting',
    enabled: userContext.hasPermission(BuilderPermission.Accounting.NESTED),
    children: [{
      name: 'draws',
      route: 'accounting/draws',
      reuseRoute: false,
      title: 'lbl_draws',
      enabled: userContext.hasPermission(BuilderPermission.Accounting.Draws.LIST),
    }, {
      name: 'xero-sync',
      route: 'accounting/xero-sync',
      reuseRoute: false,
      title: 'lbl_xero_sync',
      enabled: userContext.hasPermission(BuilderPermission.Accounting.Sync.INITIATE),
    }],
  }, {
    name: 'schedules',
    icon: 'gh:schedule',
    title: 'lbl_menu_schedules',
    enabled: userContext.hasPermission(BuilderPermission.Schedules.NESTED),
    children: [{
      name: 'sales',
      route: 'schedules/sales',
      reuseRoute: false,
      title: 'lbl_sales',
      enabled: userContext.hasPermission(BuilderPermission.Schedules.Sales.LIST),
    }, {
      name: 'pre-construction',
      route: 'schedules/pre-construction',
      reuseRoute: false,
      title: 'lbl_pre_construction',
      enabled: userContext.hasPermission(BuilderPermission.Schedules.PreConstruction.LIST),
    }, {
      name: 'construction',
      route: 'schedules/construction',
      reuseRoute: false,
      title: 'lbl_construction',
      enabled: userContext.hasPermission(BuilderPermission.Schedules.Construction.LIST),
    }, {
      name: 'maintenance',
      route: 'schedules/maintenance',
      reuseRoute: false,
      title: 'lbl_maintenance',
      enabled: userContext.hasPermission(BuilderPermission.Schedules.Maintenance.LIST),
    }],
  }, {
    name: 'estimating',
    icon: 'gh:attach-money',
    title: 'lbl_menu_estimating',
    enabled: userContext.hasPermission(BuilderPermission.Estimating.NESTED),
    children: [{
      name: 'house-plans',
      route: 'estimating/house-plans',
      reuseRoute: false,
      title: 'lbl_hp_estimate',
      enabled: userContext.hasPermission(BuilderPermission.Estimating.HouseEstimate.Plans.LIST),
    },
    {
      name: 'job-estimates',
      route: 'estimating/job-estimates',
      reuseRoute: false,
      title: 'lbl_job_estimates',
      enabled: userContext.hasPermission(BuilderPermission.Estimating.JobEstimate.LIST),
    }, {
      name: 'price_books',
      route: 'estimating/price-books',
        reuseRoute: false,
      title: 'lbl_price_books',
      enabled: userContext.hasPermission(BuilderPermission.Estimating.PriceBook.LIST),
    }, {
      name: 'bulk_update',
      route: 'estimating/bulk-update',
      title: 'lbl_bulk_update',
      enabled: userContext.hasPermission(BuilderPermission.Estimating.BulkUpdate.MANAGE),
    }, {
      name: 'bulk_delete',
      route: 'estimating/bulk-delete-hp',
      title: 'lbl_bulk_delete_house_plans',
      enabled: userContext.hasPermission(BuilderPermission.Estimating.BulkDelete.HousePlans.MANAGE),
    }],
  }, {
    name: 'manager',
    icon: 'gh:perm-identity',
    title: 'lbl_manager',
    enabled: userContext.hasPermission(BuilderPermission.Manager.NESTED)
      || userContext.hasPermission(BuilderPermission.Estimating.JobEstimate.REDO),
    children: [{
      name: 'eom-reports',
      route: 'manager/eom-reports',
      title: 'lbl_eom_reports',
      enabled: userContext.hasPermission(BuilderPermission.Manager.EomReports.MANAGE),
    }, {
      name: 'wip',
      route: 'manager/wip',
      title: 'lbl_wip_profit_adj',
      enabled: userContext.hasPermission(BuilderPermission.Manager.WipProfitAdj.MANAGE),
    }, {
      name: 'franchise-report',
      route: 'manager/franchise',
      title: 'lbl_franchise_monthly',
      enabled: userContext.hasPermission(BuilderPermission.Manager.FranchiseMonthly.MANAGE),
    }, {
      name: 'manager',
      route: 'manager/cash-flow-forecast',
      title: 'lbl_cash_flow_forecast',
      enabled: userContext.hasPermission(BuilderPermission.Manager.CashFlowForecast.MANAGE),
    }, {
      name: 'redo-job-estimate',
      route: 'manager/job-redo',
      title: 'lbl_redo_job_estimate',
      enabled: userContext.hasPermission(BuilderPermission.Estimating.JobEstimate.REDO),
    }, {
      name: 'career-enquiries',
      route: 'manager/career-enquiries',
      title: 'lbl_career_enquiries',
      enabled: userContext.hasPermission(BuilderPermission.Manager.CareerEnquiries.LIST),
    }],
  }, {
    name: 'contacts',
    icon: 'gh:people-outline',
    title: 'lbl_menu_contacts',
    route: 'contacts',
    reuseRoute: false,
    enabled: userContext.hasPermission(BuilderPermission.Contacts.NESTED),
  }, {
    name: 'application-event-log',
    icon: 'gh:ael',
    route: 'ael',
    title: 'lbl_application_event_log',
    enabled: userContext.hasPermission(BuilderPermission.EventLog.VIEW),
  }, {
    name: 'xero-issue-log',
    icon: 'gh:xero',
    route: 'accounting/xero-issue-log',
    title: 'lbl_xero_issues_log',
    enabled: userContext.hasPermission(BuilderPermission.XeroIssueLog.VIEW),
  }, {
    name: 'bulk-update-tasks-log',
    icon: 'gh:calendar',
    route: 'butl',
    title: 'lbl_bu_tasks_log',
    enabled: userContext.hasPermission(BuilderPermission.BulkUpdateTaskLog.VIEW),
  }, {
    name: 'schedule-tasks-log',
    icon: 'gh:stl',
    route: 'stl',
    title: 'lbl_scheduled_tasks_log',
    enabled: userContext.hasPermission(BuilderPermission.ScheduleTaskLog.VIEW),
  }, {
    name: 'setup',
    icon: 'gh:setup',
    title: 'lbl_setup',
    enabled: userContext.hasPermission(BuilderPermission.Setup.NESTED),
    children: [{
      name: 'system-variables',
      route: 'setup/system-variables',
      title: 'lbl_system_variables',
      enabled: userContext.hasPermission(BuilderPermission.Setup.SystemVariables.NESTED),
    }, {
      name: 'estimating',
      title: 'lbl_menu_estimating',
      route: 'setup/estimating',
      enabled: userContext.hasPermission(BuilderPermission.Setup.Estimating.NESTED),
    }, {
      name: 'sub-offices',
      title: 'lbl_menu_sub_offices',
      route: 'setup/sub-offices',
      enabled: userContext.hasPermission(BuilderPermission.Setup.SystemVariables.Franchisee.VIEW),
    }, {
      name: 'letter-templates',
      title: 'lbl_manage_letter_templates',
      route: 'setup/letter-templates',
      enabled: userContext.hasPermission(BuilderPermission.Setup.ManageTemplates.NESTED),
    }, {
      name: 'sms-templates',
      title: 'lbl_manage_sms_templates',
      route: 'setup/sms-templates',
      enabled: userContext.hasPermission(BuilderPermission.Setup.SmsTemplates.LIST),
    }, {
      name: 'templates',
      title: 'lbl_manage_email_templates',
      route: 'setup/email-templates',
      enabled: userContext.hasPermission(BuilderPermission.Setup.Templates.NESTED),
    }, {
      name: 'templates',
      title: 'lbl_mobile_app_checklists',
      route: 'setup/checklists',
      enabled: (isCorpOrMaster(userContext) || userContext.hasPermission(BuilderPermission.Setup.Checklists.NESTED)),
    }, {
      name: 'email-recipients',
      title: 'lbl_email_recipients',
      route: 'setup/email-recipients',
      enabled: userContext.hasPermission(BuilderPermission.Setup.EmailRecipients.VIEW)
        || userContext.hasPermission(BuilderPermission.Setup.EmailRecipients.EDIT),
    }, {
      name: 'milestone-events',
      title: 'lbl_milestone_events',
      route: 'setup/milestone-events',
      enabled: userContext.hasPermission(BuilderPermission.Setup.MilestoneEvents.NESTED),
    }, {
      name: 'sales',
      route: 'setup/sales',
      title: 'lbl_menu_sales',
      enabled: userContext.hasPermission(BuilderPermission.Setup.Sales.NESTED),
    }, {
      name: 'job-admin',
      title: 'lbl_menu_job_admin',
      route: 'setup/job-admin',
      enabled: userContext.hasPermission(BuilderPermission.Setup.JobAdmin.NESTED),
    }, {
      name: 'accounting',
      title: 'lbl_menu_accounting',
      route: 'setup/accounting',
      enabled: userContext.hasPermission(BuilderPermission.Setup.Accounting.NESTED),
    }, {
      name: 'schedules',
      title: 'lbl_menu_schedules',
      route: 'setup/scheduling',
      enabled: userContext.hasPermission(BuilderPermission.Setup.Schedules.NESTED),
    }, {
      name: 'manager',
      title: 'lbl_manager',
      route: 'setup/manager',
      enabled: userContext.hasPermission(BuilderPermission.Setup.Manager.NESTED),
    }, {
      name: 'contacts',
      route: 'setup/contacts',
      title: 'lbl_menu_contacts',
      enabled: userContext.hasPermission(BuilderPermission.Setup.Contacts.NESTED),
    },{
      name: 'Billing & Invoices',
      onClick: () => injector.get(BillingInvoicesRedirectBusinessService).redirectToBilling(),
      title: 'lbl_menu_billing_&_invoices',
      enabled: userContext.hasPermission(BuilderPermission.Setup.ManageBilling.VIEW)
        || userContext.hasPermission(BuilderPermission.Setup.ManageBilling.MANAGE),
    }],
  }];
}

function buildAdminNavigationMenu(userContext: BuilderUserContext, injector: Injector): NavigationItemTree[] {
  return [{
    name: 'dashboard',
    icon: 'gh:dashboard',
    title: 'lbl_menu_dashboard',
    route: 'dashboard',
  }, {
    name: 'contracts-manager',
    icon: 'gh:power-settings-new',
    title: 'lbl_contracts_manager',
    route: 'contracts-manager',
    reuseRoute: false,

  }, {
    name: 'sales',
    icon: 'gh:money-off',
    title: 'lbl_menu_sales',
    children: [{
      name: 'leads',
      title: 'lbl_leads',
      route: 'leads',
      reuseRoute: false,
    }, {
      name: 'hlp',
      title: 'lbl_hl',
      route: 'house-land',
      reuseRoute: false,
    }],
  }, {
    name: 'master-zones',
    icon: 'gh:master-zones',
    route: 'master-zones',
    title: 'lbl_menu_master_zones',
    enabled: userContext.corporateView,
  }, {
    name: 'contacts',
    icon: 'gh:contacts',
    title: 'lbl_menu_contacts',
    route: 'contacts',
    reuseRoute: false,
  }, {
    name: 'switch-to',
    icon: 'gh:switch-to',
    route: 'switch-to',
    title: 'lbl_menu_switch_to',
  }, {
    name: 'authorization-numbers',
    icon: 'gh:authorization-numbers',
    onClick: () => {
      injector.get(DialogService).openWithInput(authNumberGenerationDialog, <AuthNumberGenerationConfig>{});
    },
    title: 'lbl_menu_authorization_numbers',
  }, {
    name: 'send-house-plans',
    icon: 'gh:vertical-align-top',
    route: 'send-to-office',
    title: 'lbl_send_to_office',
    enabled: userContext.masterView,
  }, {
    name: 'application-event-log',
    icon: 'gh:ael',
    route: 'ael',
    title: 'lbl_application_event_log',
  }, {
    name: 'bulk-update-tasks-log',
    icon: 'gh:calendar',
    route: 'butl',
    title: 'lbl_bu_tasks_log',
  }, {
    name: 'schedule-tasks-log',
    icon: 'gh:stl',
    route: 'stl',
    title: 'lbl_scheduled_tasks_log',
  }, {
    name: 'setup',
    icon: 'gh:setup',
    title: 'lbl_setup',
    children: [{
      name: 'system-variables-master-zone',
      title: 'lbl_system_variables',
      route: 'setup/system-variables/master-zone',
      enabled: userContext.masterView,
    }, {
      name: 'sub-offices',
      title: 'lbl_menu_sub_offices',
      route: 'setup/sub-offices',
      enabled: userContext.masterView || userContext.officeView ,
    }, {
      name: 'license-agreements',
      title: 'lbl_license_agreements',
      route: 'setup/license-agreements',
      enabled: userContext.corporateView,
    }, {
      name: 'product',
      title: 'Subscription Plans',
      route: 'setup/product',
      enabled: userContext.corporateView,
    }, {
      name: 'base-designs',
      title: 'lbl_base_designs',
      route: 'setup/base-designs',
      enabled: userContext.corporateView || userContext.masterView,
    }, {
      name: 'users',
      title:  userContext.corporateView ? 'lbl_menu_corporate_users' : userContext.masterView ?  'lbl_menu_master_users' : 'lbl_menu_users',
      route: 'setup/users',
    }, {
        name: 'office-users',
        title: 'lbl_office_users',
        route: 'setup/office-users',
        enabled: userContext.corporateView || userContext.masterView ,
    }, {
      name: 'office-permissions',
      title: 'lbl_menu_office_permissions',
      route: 'setup/office-permissions',
      enabled: userContext.corporateView,
    }, {
      name: 'offices',
      title: 'lbl_menu_offices',
      route: 'setup/offices',
      enabled: userContext.corporateView || userContext.masterView,
    }, {
      name: 'letter-templates',
      title: 'lbl_manage_letter_templates',
      route: 'setup/letter-templates',
    }, {
      name: 'sms-templates',
      title: 'lbl_manage_sms_templates',
      route: 'setup/sms-templates',
    }, {
      name: 'templates',
      title: 'lbl_manage_email_templates',
      route: 'setup/email-templates',
    }, {
      name: 'templates',
      title: 'lbl_mobile_app_checklists',
      route: 'setup/checklists',
    }, {
      name: 'email-recipients',
      title: 'lbl_email_recipients',
      route: 'setup/email-recipients',
    }, {
      name: 'rfq-structure',
      title: 'lbl_rfq_structure',
      route: 'setup/rfq-structure',
      enabled: userContext.masterView,
    }, {
      name: 'color-schema',
      title: 'lbl_menu_color_schema',
      enabled: userContext.corporateView,
      onClick: () => {
        const translations = injector.get(TranslationService);
        const rootLayout = injector.get(RootLayout);
        rootLayout.showSidebar({
          title: translations.get('lbl_select_color_schema'),
          component: ColorSchemePickerComponent,
        });
      },
    }, {
      name: 'report-settings',
      title: 'lbl_reports_settings',
      route: 'setup/report-settings',
    }],
  }];
}
